// Every report, once: its address, its title and what kind of page it is.
//
// The URL is /reports/<slug>. Titles use the reporting glossary's words (see
// the organization service's field registry), in sentence case, and are what
// the header, the sidebar buttons and the downloaded file names show.
//
// `pivot` makes it a pivot report: the rows and columns it asks
// POST /organization/reports/pivot for, shown in PivotTable. `siteAxis` marks
// its collection-site dimension, which the user can switch to county or site
// group (SITE_DIMENSIONS).
//
// `legacyButton` is the ?button= value the page used to be opened with, kept
// so bookmarked links still land on the right report.
//
// `listed: false` keeps a page out of the sidebar and the report menu (the
// data dictionary, opened from "How this is calculated"). `comingSoon` lists
// a report that cannot be opened yet, greyed out.

export const REPORT_GROUPS = {
  tables: "Reports",
  charts: "Graphs",
  exports: "Exports",
};

export const REPORT_DEFINITIONS = [
  {
    slug: "raw-data",
    title: "Raw data",
    shortTitle: "Raw data",
    group: "tables",
    kind: "table",
    explains: "One row per specimen with every field the data dictionary describes, filtered like any report.",
    legacyButton: "Raw-Data",
  },
  {
    slug: "species-summary",
    title: "Species summary among collection sites",
    shortTitle: "Species summary among collection sites",
    group: "tables",
    kind: "table",
    pivot: { rows: ["species"], columns: ["collectionSite"] },
    siteAxis: { axis: "columns", index: 0 },
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Counts are by species and collection site, over the collection dates in the filter.",
    fieldsUsed: ["summary_species", "collection_site", "report_date"],
    legacyButton: "Species-Summary-Among-Collections",
  },
  {
    slug: "weekly-species",
    title: "Weekly species report",
    shortTitle: "Weekly species report",
    group: "tables",
    kind: "table",
    pivot: { rows: ["week", "collectionSite"], columns: ["species"] },
    siteAxis: { axis: "rows", index: 1 },
    view: "weekly",
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Weeks start on the organization's chosen day. Weekly change is the site's total minus its total the week before; a week with no specimens counts as 0.",
    fieldsUsed: ["report_date", "collection_site", "summary_species"],
    legacyButton: "Weekly-Species-report",
  },
  {
    slug: "species-by-week",
    title: "Species by week",
    shortTitle: "Species by week",
    group: "tables",
    kind: "table",
    pivot: { rows: ["species"], columns: ["week"] },
    view: "speciesByWeek",
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Week 1 is the week holding January 1, starting on the organization's chosen day.",
    fieldsUsed: ["summary_species", "report_date"],
  },
  {
    slug: "mda",
    title: "MDA",
    shortTitle: "MDA",
    group: "exports",
    kind: "template",
    // the server-side template (services/organization reporting/templates)
    template: "mda",
    explains: "One row per county, collection site, trap type, collection date, genus, species and sex, with its count. Epi Week is the CDC (MMWR) week of the collection date.",
    fieldsUsed: ["collection_site_county", "collection_site", "collection_method_display_name", "specimen_capture_date", "final_genus", "final_species", "final_sex"],
  },
  {
    // waiting on VectorSurv's column specification
    slug: "vectorsurv",
    title: "VectorSurv",
    shortTitle: "VectorSurv",
    group: "exports",
    kind: "template",
    comingSoon: true,
  },
  {
    slug: "species-pie",
    title: "Species summary",
    shortTitle: "Species summary",
    group: "charts",
    kind: "chart",
    icon: "pie",
    pivot: { rows: ["genus", "species"], columns: [] },
    view: "pie",
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Shares are of the specimens shown; hiding a species takes it out of every share.",
    fieldsUsed: ["final_genus", "summary_species"],
    legacyButton: "Pie-chart",
  },
  {
    slug: "species-bar",
    title: "Species summary among collection sites",
    shortTitle: "Species summary among collection sites",
    group: "charts",
    kind: "chart",
    icon: "bar",
    pivot: { rows: ["collectionSite"], columns: ["species"] },
    siteAxis: { axis: "rows", index: 0 },
    view: "stackedBar",
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Specimens from trays with no collection site are not drawn; the note under the chart counts them.",
    fieldsUsed: ["collection_site", "summary_species"],
    // what the chart's toggle calls the pivot and its swap
    views: ["By collection site", "By species"],
    legacyButton: "Bar-chart",
  },
  {
    slug: "species-abundance",
    title: "Species abundance",
    shortTitle: "Species abundance",
    group: "charts",
    kind: "chart",
    icon: "line",
    // over time: the x-axis is the chosen grain (see `grains`)
    pivot: { rows: ["month"], columns: ["species"] },
    view: "abundance",
    grains: ["week", "month", "year"],
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Periods are by collection date, falling back to the imaging date for trays with no collection date.",
    fieldsUsed: ["summary_species", "report_date"],
    legacyButton: "LineGraph-chart",
  },
  {
    slug: "dictionary",
    title: "Data dictionary",
    shortTitle: "Data dictionary",
    kind: "dictionary",
    listed: false,
  },
  {
    slug: "yearly-trends",
    title: "Yearly trends",
    shortTitle: "Yearly trends",
    group: "charts",
    kind: "chart",
    icon: "trend",
    pivot: { rows: ["weekOfYear"], columns: ["year", "species"] },
    view: "yearlyTrends",
    explains: "Each specimen counts once, by its final result: the contributed ID when a person entered one, otherwise the algorithm's. Weeks are fixed blocks of the calendar, January 1 to 7 being week 1 every year, so the same week means the same dates each year.",
    fieldsUsed: ["summary_species", "report_date"],
  },
];

// what can be opened: everything but the reports still to come
const BY_SLUG = Object.fromEntries(REPORT_DEFINITIONS.filter((d) => !d.comingSoon).map((d) => [d.slug, d]));
const BY_LEGACY = Object.fromEntries(
  REPORT_DEFINITIONS.filter((d) => d.legacyButton).map((d) => [d.legacyButton, d])
);

/** The reports a group lists, in order: the sidebar's buttons and the menu's items. */
export const listedReports = (group) => REPORT_DEFINITIONS.filter((d) => d.group === group && d.listed !== false);

export const reportDefinition = (slug) => BY_SLUG[slug] ?? null;

// what a report's site dimension can be
export const SITE_DIMENSIONS = [
  { value: "collectionSite", label: "Collection site" },
  { value: "county", label: "County" },
  { value: "siteGroup", label: "Site group" },
];

/** The pivot a definition asks for, its site dimension switched to `siteDim`. */
export const pivotFor = (definition, { siteDim, grain } = {}) => {
  if (!definition?.pivot) return null;
  const pivot = { ...definition.pivot, rows: [...definition.pivot.rows], columns: [...definition.pivot.columns] };
  if (definition.grains && grain) pivot.rows = [grain];
  if (definition.siteAxis && siteDim) pivot[definition.siteAxis.axis][definition.siteAxis.index] = siteDim;
  return pivot;
};

export const definitionForLegacyButton = (button) => BY_LEGACY[button] ?? null;

